import { useState } from 'react'
import { creerCompteLivreur, initiales, modifierOrganisation, type IdentifiantsLivreur } from '../../api/operations'
import { depuis, etatPosition } from '../../api/geo'
import { useDroits } from '../Contexte'
import type { DonneesOps, StatutLivreur } from '../../api/types'
import { Icone } from '../Icone'
import { Modale } from '../Modale'
import { aMotDePasseTemporaire, phraseSansMotDePasse } from '../../api/identifiants'
import { ChampTelephone, afficherTelephone, useTelephone, validerContact } from '../../api/commun'
import { supabase } from '../../api/supabase'

/**
 * Livreurs — TRANSPOSITION de `renderDrivers` + `renderJoinCode` +
 * `copyJoinCode` + `openDrvModal` (console HTML, `web/operateur/index.html`).
 *
 * Le bouton « Ajouter un livreur » de cet écran crée un COMPTE complet
 * (Auth + profil + membership driver + fiche), comme l'original : c'est la
 * seule action que `openDrvModal` proposait ici, via `creerCompteLivreur`.
 * `ajouterLivreur` (fiche seule, sans compte, définie dans operations.ts) n'est
 * appelée par aucun bouton de cet écran dans la source — elle sert un autre
 * usage, pas celui-ci.
 */

const STATUTS_LIVREUR: Record<StatutLivreur, readonly [string, string]> = {
  available: ['Disponible', 'b-green'],
  on_course: ['En course', 'b-gold'],
  offline: ['Hors ligne', 'b-gray'],
}

export function EcranLivreurs({
  donnees,
  orgId,
  surRafraichir,
  surNotifier,
}: {
  donnees: DonneesOps
  orgId: string
  surRafraichir: () => Promise<void>
  surNotifier: (message: string) => void
}) {
  const [modaleOuverte, setModaleOuverte] = useState(false)
  const [identifiants, setIdentifiants] = useState<{ nom: string; cred: IdentifiantsLivreur } | null>(null)
  const droits = useDroits()
  const [bascule, setBascule] = useState(false)

  async function basculerPreuve() {
    const cible = !donnees.org.require_delivery_proof
    const message = cible
      ? "Exiger une photo à chaque livraison ?\n\nLes livreurs ne pourront plus clore une course sans photographier la remise, et le dispatch ne pourra plus passer une course à « Livrée » depuis la console."
      : 'Ne plus exiger de photo à la livraison ?'
    if (!window.confirm(message)) return
    setBascule(true)
    try {
      await modifierOrganisation(orgId, { require_delivery_proof: cible })
      surNotifier(cible ? 'Photo de remise désormais exigée' : 'Photo de remise facultative')
      await surRafraichir()
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBascule(false)
    }
  }

  return (
    <>
      <CarteCodeEquipe code={donnees.org.driver_join_code ?? null} surNotifier={surNotifier} />

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="check" classe="h-ic" /> Preuve de livraison
            </h3>
            <div className="ch-sub">
              Contrôlée par le serveur (fonction <span className="mono">set-course-status</span>), pas par l’app : une app ancienne ne peut pas la contourner.
            </div>
          </div>
        </div>
        <div className="card-body">
          <button
            type="button"
            className={`interrupteur${donnees.org.require_delivery_proof ? ' on' : ''}`}
            role="switch"
            aria-checked={donnees.org.require_delivery_proof}
            onClick={() => void basculerPreuve()}
            disabled={!droits.peut('parametres_org') || bascule}
          >
            <span className={`bascule${donnees.org.require_delivery_proof ? ' on' : ''}`} aria-hidden="true" />
            <span>
              <b>Exiger une photo à la livraison</b> — {donnees.org.require_delivery_proof ? 'activé' : 'désactivé'}
            </span>
          </button>
          {!droits.peut('parametres_org') && (
            <p className="t-muted" style={{ marginTop: 8 }}>
              {droits.refus} Seul le propriétaire change ce réglage.
            </p>
          )}
        </div>
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="user" classe="h-ic" /> Nos livreurs
            </h3>
            <div className="ch-sub">
              {donnees.drivers.length} livreur{donnees.drivers.length > 1 ? 's' : ''} · flotte en propre
            </div>
          </div>
          <span className="sp" />
          {droits.peut('livreurs') && (
            <button className="btn btn-gold btn-sm" onClick={() => setModaleOuverte(true)}>
              <Icone nom="plus" /> Ajouter un livreur
            </button>
          )}
        </div>
        <div className="card-body flush">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>
                  <th>Livreur</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Position</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {donnees.drivers.map((d) => {
                  const [libelle, couleur] = STATUTS_LIVREUR[d.status]
                  return (
                    <tr key={d.id}>
                      <td>
                        <div className="u-cell">
                          <span className="avatar">{initiales(d.full_name)}</span>
                          <span className="t-strong">{d.full_name}</span>
                        </div>
                      </td>
                      <td className="mono">{d.phone ?? '—'}</td>
                      <td>
                        <span className={`badge ${couleur}`}>{libelle}</span>
                      </td>
                      <td className="t-muted">{texteGps(d)}</td>
                      <td>
                        <span style={{ color: 'var(--gold)' }}>
                          {Array.from({ length: Math.round(d.rating) }).map((_, i) => (
                            <Icone key={i} nom="star" />
                          ))}
                        </span>
                        <span className="t-muted"> {d.rating.toFixed(1)}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modaleOuverte && (
        <ModaleLivreur
          orgId={orgId}
          surFermer={() => setModaleOuverte(false)}
          surNotifier={surNotifier}
          surCree={async (nom, cred) => {
            setModaleOuverte(false)
            setIdentifiants({ nom, cred })
            await surRafraichir()
          }}
        />
      )}

      {/* ⚠️ TRANSPOSITION FIDÈLE d'un comportement un peu étrange de la source :
          `openModal` réinitialise TOUJOURS le bouton droit à « Enregistrer »,
          y compris pour cette modale de simple consultation — dont le bouton
          « Enregistrer » ne fait que fermer, exactement comme « Annuler ». On
          reproduit ça au caractère près plutôt que de le « corriger »
          silencieusement (ce serait s'écarter de la source sans que personne
          l'ait demandé). */}
      {identifiants && (
        <Modale
          titre="Compte livreur créé"
          ouverte
          surFermer={() => setIdentifiants(null)}
          surEnregistrer={() => setIdentifiants(null)}
        >
          <div style={{ fontSize: 13.5, lineHeight: 1.65 }}>
            <b>{identifiants.nom}</b> peut maintenant se connecter à l'<b>app Livreur</b> :
            <div
              style={{
                background: 'var(--gold-wash)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '12px 14px',
                margin: '12px 0',
              }}
            >
              {identifiants.cred.email && (
                <div>
                  E-mail : <b>{identifiants.cred.email}</b>
                </div>
              )}
              {identifiants.cred.phone && (
                <div>
                  Numéro WhatsApp : <b>{afficherTelephone(identifiants.cred.phone)}</b>
                </div>
              )}
              {aMotDePasseTemporaire(identifiants.cred) ? (
                <div>
                  Mot de passe temporaire : <b>{identifiants.cred.temp_password}</b>
                </div>
              ) : (
                <div>{phraseSansMotDePasse(identifiants.cred)}</div>
              )}
            </div>
            {aMotDePasseTemporaire(identifiants.cred) && 'Notez ces accès maintenant — le mot de passe ne sera plus affiché.'}
          </div>
        </Modale>
      )}
    </>
  )
}

function texteGps(d: DonneesOps['drivers'][number]): string {
  const p = etatPosition(d)
  if (p.etat === 'aucune') return 'Jamais localisé'
  return p.etat === 'recente' ? `En direct (${depuis(p.ageMs)})` : `Ancienne (${depuis(p.ageMs)})`
}

/** `renderJoinCode` : rien à afficher tant que l'org n'a pas de code (org non
 * encore migrée, ou champ vide) — l'original vidait `#drv-joincode` dans ce cas. */
function CarteCodeEquipe({ code, surNotifier }: { code: string | null; surNotifier: (message: string) => void }) {
  if (!code) return null
  return (
    <div className="joincode-card">
      <div className="jc-ic">
        <Icone nom="user" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="jc-h">Code équipe livreurs</div>
        <div className="jc-sub">
          Partagez ce code à vos livreurs : ils créent leur compte eux-mêmes depuis l'app{' '}
          <b>SwiftDeliver Livreur</b>, onglet « Rejoindre une équipe ». Inutile de leur créer un accès à la main.
        </div>
      </div>
      <div className="jc-code">{code}</div>
      <button className="btn btn-gold btn-sm" onClick={() => copierCode(code, surNotifier)}>
        Copier
      </button>
    </div>
  )
}

/** `copyJoinCode` : le toast part même si le presse-papiers échoue (contexte
 * non sécurisé, permission refusée) — le code reste lisible à l'écran. */
function copierCode(code: string, surNotifier: (message: string) => void) {
  try {
    void navigator.clipboard.writeText(code)
  } catch {
    /* Volontairement ignoré, comme dans `copyJoinCode` d'origine. */
  }
  surNotifier(`Code équipe copié : ${code}`)
}

function ModaleLivreur({
  orgId,
  surFermer,
  surNotifier,
  surCree,
}: {
  orgId: string
  surFermer: () => void
  surNotifier: (message: string) => void
  surCree: (nom: string, cred: IdentifiantsLivreur) => Promise<void>
}) {
  const [prenom, setPrenom] = useState('')
  const [nom, setNom] = useState('')
  const [email, setEmail] = useState('')
  const { valeur: telephone, setValeur: setTelephone } = useTelephone(supabase)
  const [erreurContact, setErreurContact] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  async function enregistrer() {
    const first = prenom.trim()
    const last = nom.trim()
    if (!first || !last) {
      surNotifier('Prénom et nom obligatoires')
      return
    }
    /* E-mail OU numéro WhatsApp : au moins un des deux (l'e-mail reste recommandé). */
    const contact = validerContact({ email, telephone })
    if (!contact.ok) {
      setErreurContact(contact.message)
      return
    }
    setErreurContact(null)
    setEnCours(true)
    let cred: IdentifiantsLivreur
    try {
      cred = await creerCompteLivreur(orgId, { first_name: first, last_name: last, contact })
    } catch (e) {
      surNotifier(`Erreur : ${e instanceof Error ? e.message : String(e)}`)
      setEnCours(false)
      return
    }
    setEnCours(false)
    await surCree(`${first} ${last}`, cred)
  }

  return (
    <Modale
      titre="Ajouter un livreur"
      ouverte
      surFermer={surFermer}
      surEnregistrer={() => void enregistrer()}
      enCours={enCours}
    >
      <div style={{ display: 'flex', gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Prénom</label>
          <input value={prenom} onChange={(e) => setPrenom(e.target.value)} placeholder="ex. Moussa" />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Nom</label>
          <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="ex. Traoré" />
        </div>
      </div>
      <div className="field">
        <label htmlFor="lv-email">E-mail (recommandé)</label>
        <input
          id="lv-email"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setErreurContact(null) }}
          placeholder="livreur@exemple.com"
        />
      </div>
      <div style={{ marginBottom: 14 }}>
        <ChampTelephone
          valeur={telephone}
          surChangement={(v) => { setTelephone(v); setErreurContact(null) }}
          etiquette="Numéro WhatsApp"
          montrerErreur
          aide="Au moins un des deux : e-mail ou numéro. Avec un numéro, le livreur se connecte avec un code reçu sur WhatsApp."
        />
      </div>
      {erreurContact && (
        <p role="alert" style={{ color: 'var(--err)', fontSize: 12.5, margin: '0 0 8px' }}>
          {erreurContact}
        </p>
      )}
      <div
        style={{
          fontSize: 12,
          color: 'var(--muted)',
          background: 'var(--gold-wash)',
          borderRadius: 8,
          padding: '8px 10px',
          marginTop: 4,
        }}
      >
        Un <b>compte livreur</b> sera créé. Avec un e-mail, transmettez-lui l'e-mail + le mot de passe temporaire
        affichés à l'étape suivante ; avec un numéro seul, il se connecte avec son numéro WhatsApp.
      </div>
    </Modale>
  )
}
